import { useEffect } from 'react';

import { equals } from 'ramda';
import { useDispatch, useSelector } from 'react-redux';

import { mappedSearch } from '../actions/mapped-search';
import { getCanonical } from '../helpers/seo';
import { RootState } from '../reducers';
import {
  getCurrentPage,
  getFacets,
  getFullResultsByTheirOwnIdentifier,
  getNextStatus,
  getOptionsFilters,
  getPreviousStatus,
  getResultsCount,
  getTotalPage,
  hasReachedEnd,
  isAnyPageError,
  isAnyPageLoading,
} from '../selectors/search';

const useConnectedGrid = <T = any>(
  key: string,
  page: number,
  filters: Record<string, any> = {},
  reset = true
) => {
  const dispatch = useDispatch();

  const data: T = useSelector(getFullResultsByTheirOwnIdentifier(key));
  const isLoading: boolean = useSelector((state: RootState) =>
    isAnyPageLoading(key, state)
  );
  const error = useSelector((state: RootState) => isAnyPageError(key, state));
  const previousStatus = useSelector((state: RootState) =>
    getPreviousStatus(key, state)
  );
  const nextStatus = useSelector((state: RootState) =>
    getNextStatus(key, state)
  );
  const isEndReached = useSelector((state: RootState) =>
    hasReachedEnd(key, state)
  );
  const count = useSelector((state: RootState) => getResultsCount(key, state));
  const facets: Record<string, any> = useSelector((state: RootState) =>
    getFacets(key, state)
  );
  const totalPage = useSelector((state: RootState) => getTotalPage(key, state));
  const currentPage: number =
    useSelector<RootState, number>(getCurrentPage(key)) || 1;
  const canonical = useSelector((state: RootState) =>
    getCanonical(state.locale, state.main?.document)
  );
  const stateFilters = useSelector((state: RootState) =>
    getOptionsFilters(key, state)
  );

  useEffect((): void => {
    if (
      !isLoading &&
      !error &&
      (!equals(filters, stateFilters) || page !== currentPage)
    ) {
      dispatch(mappedSearch[key](key, page, { query: filters }, reset));
    }
  }, [
    dispatch,
    error,
    filters,
    isLoading,
    key,
    page,
    currentPage,
    stateFilters,
    reset,
  ]);

  return {
    data,
    isLoading,
    error,
    previousStatus,
    nextStatus,
    hasReachedEnd: isEndReached,
    count,
    facets,
    totalPage,
    canonical,
    page,
    filters,
  };
};

export default useConnectedGrid;
